import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import EnquiryForm from "@/components/EnquiryForm";

// Opens the enquiry popup at most ONCE per session, and only after genuine
// engagement — 15s dwell time, 55% scroll depth, or exit-intent (cursor
// leaving the viewport top). Never nags: once shown or dismissed, it's done
// for the session.
export default function AutoEnquiryPopup() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    if (sessionStorage.getItem("tx_popup_seen")) return;
    let shown = false;
    let timer;

    const cleanup = () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onExitIntent);
      clearTimeout(timer);
    };
    const show = () => {
      if (shown || sessionStorage.getItem("tx_popup_seen")) return;
      shown = true;
      sessionStorage.setItem("tx_popup_seen", "1");
      cleanup();
      setOpen(true);
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.55) show();
    };
    const onExitIntent = (e) => {
      if (!e.relatedTarget && e.clientY <= 8) show();
    };

    timer = setTimeout(show, 15000);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onExitIntent);
    return cleanup;
  }, [isAdmin]);

  const handleChange = (v) => {
    setOpen(v);
    if (!v) sessionStorage.setItem("tx_popup_seen", "1");
  };

  if (isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={handleChange}>
      <DialogContent
        data-testid="auto-enquiry-popup"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--tx-bg-2)] border-[var(--tx-border)] text-[var(--tx-text)]"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-[var(--tx-gold)]" /> Partner with TripTrix
          </DialogTitle>
          <DialogDescription className="text-[var(--tx-text-2)] text-sm">
            Travel agent or tour operator? Send us your query and our trade desk shares net rates &
            a tailored itinerary within 24 hours.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <EnquiryForm />
        </div>
      </DialogContent>
    </Dialog>
  );
}
